import { Alert, Box, Chip, Grid, IconButton, Link, Paper, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Tooltip, styled } from "@mui/material";
import { ExpandMore as ExpandMoreIcon, Close as CloseIcon } from '@mui/icons-material';
import { Profile, RevisorData } from "./types";
import TimeSeriesChart from "../../components/timeSeriesGraph";
import RelationshipsTable from '../../components/RelationshipsTable';
import { Edge } from 'org-graph/types';
import { GraphMonitoringEntityDetail } from "../GraphMonitoring/types";
import { formatDateTime, formatRelativeTime, humanizeValue } from "../GraphMonitoring/utils";
import { Trans, useTranslation } from 'react-i18next';
import { getCompanyStatus, getMonitoringRuleName } from '../../i18n/backendTranslations';
import appI18n from '../../i18n/i18n';
import { compareDisplayText, formatNumber } from '../../i18n/formatters';
import { getProfileIvoLicenses } from './ivoLicenses';
import EmploymentSupportCard from './EmploymentSupportCard';
import { hasEmploymentSupportData } from './employmentSupport';
import { ModernAccordion, ModernAccordionSummary, ModernAccordionDetails } from './CompanyDetailsAccordion';

export function AuditorName({ revisor }: { revisor: RevisorData }) {
  const name = revisor.revisor_name?.trim() || '-';
  const number = revisor.revisor_personal_number?.replace(/[-+\s]/g, '') || '';
  const shortNumber = number.length === 12 ? number.slice(2) : number;
  // Organization numbers have a month part of 20 or higher.
  if (!/^\d{10}$/.test(shortNumber) || !/^(0[1-9]|1[0-2])$/.test(shortNumber.slice(2, 4))) {
    return <>{name}</>;
  }
  const currentYear = new Date().getFullYear() % 100;
  const fullNumber = number.length === 12 ? number : `${Number(number.slice(0, 2)) > currentYear ? '19' : '20'}${number}`;
  return <Link href={`/personsearch?pnr=${fullNumber}`} target="_blank" rel="noopener noreferrer" underline="hover">{name}</Link>;
}


const DataLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.6875rem',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.5),
  fontWeight: 500,
}));

const DataValue = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  color: theme.palette.text.primary,
  fontWeight: 500,
  fontVariantNumeric: 'tabular-nums',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
  paddingBottom: theme.spacing(0.75),
}));

const MonitoringToggleCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1.5),
  width: '100%',
  padding: theme.spacing(1.1, 1.25),
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.03)'
    : 'rgba(0, 0, 0, 0.015)',
  transition: 'background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
}));

const MonitoringNotificationCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1, 1.25),
  boxShadow: 'none',
  borderRadius: 10,
  borderColor: theme.palette.divider,
}));

function formatAmount(amount: string | undefined): string {
  if (!amount) {
    return '';
  }
  // Parse the string to a number
  const numberValue = parseFloat(amount.replace(/,/g, ''));

  // Check if the parsed value is a valid number
  if (isNaN(numberValue)) {
    return amount; // Return the original string if it's not a valid number
  }

  return formatNumber(numberValue, appI18n.resolvedLanguage);
}

function getDisplayValue(value: string | undefined): string {
  return value && value.trim() ? value : '-';
}

function getLocality(postalCode: string | undefined, town: string | undefined): string {
  return [postalCode, town].filter((value): value is string => Boolean(value && value.trim())).join(' ');
}

export function formatCompanyAddress(profile: Pick<Profile, 'coAddress' | 'postalAddress' | 'postalCode' | 'town'>): string {
  const addressParts = [
    profile.coAddress?.trim(),
    profile.postalAddress?.trim(),
    getLocality(profile.postalCode, profile.town),
  ].filter((value): value is string => Boolean(value && value.trim()));

  const uniqueAddressParts = addressParts.filter((part, index) =>
    addressParts.findIndex((candidate) => candidate.toLowerCase() === part.toLowerCase()) === index,
  );

  return uniqueAddressParts.length > 0 ? uniqueAddressParts.join(', ') : '-';
}

export function getCompanyDetailsDateLabel(profile: Pick<Profile, 'companyDetailsSource'>): string {
  return profile.companyDetailsSource === 'stiftelser' ? 'First seen' : 'Registration Date';
}

function getNotificationDiffs(detail: GraphMonitoringEntityDetail | null) {
  return detail?.diffs.filter((diff) => diff.ruleMatches.length > 0 || diff.shouldNotify) ?? [];
}

function getTriggeredRuleLabels(detail: GraphMonitoringEntityDetail | null) {
  if (!detail) {
    return [];
  }

  const ruleLookup = new Map(
    detail.ruleSubscriptions.map((rule) => [rule.ruleKey, rule.displayName]),
  );

  return Array.from(
    new Set(
      getNotificationDiffs(detail).flatMap((diff) =>
        diff.ruleMatches.map((ruleKey) => getMonitoringRuleName(ruleKey, ruleLookup.get(ruleKey) ?? humanizeValue(ruleKey))),
      ),
    ),
  ).sort((left, right) => compareDisplayText(left, right, appI18n.resolvedLanguage));
}

function getDiffSummaryRows(diff: GraphMonitoringEntityDetail['diffs'][number]) {
  return [
    {
      label: 'Changed',
      nodes: diff.diff.changed_nodes.length,
      relationships: diff.diff.changed_relationships.length,
    },
    {
      label: 'Added',
      nodes: diff.diff.added_nodes.length,
      relationships: diff.diff.added_relationships.length,
    },
    {
      label: 'Removed',
      nodes: diff.diff.removed_nodes.length,
      relationships: diff.diff.removed_relationships.length,
    },
  ];
}

function MonitoringRuleList({
  title,
  labels,
}: {
  title: string;
  labels: string[];
}) {
  const { t } = useTranslation();
  if (labels.length === 0) {
    return null;
  }

  return (
    <Box
      sx={(theme) => ({
        p: 1,
        borderRadius: 2,
        border: '1px solid',
        borderColor:
          theme.palette.mode === 'dark'
            ? 'rgba(148, 163, 184, 0.18)'
            : 'rgba(15, 23, 42, 0.10)',
        backgroundColor:
          theme.palette.mode === 'dark'
            ? 'rgba(148, 163, 184, 0.07)'
            : 'rgba(15, 23, 42, 0.03)',
      })}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          mb: 0.75,
          flexWrap: 'wrap',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontSize: '0.62rem',
            color: 'text.secondary',
          }}
        >
          {title}
        </Typography>
        <Chip
          label={t('common.counts.rule', { count: labels.length })}
          size="small"
          sx={(theme) => ({
            height: 20,
            backgroundColor: 'transparent',
            border: '1px solid',
            borderColor:
              theme.palette.mode === 'dark'
                ? 'rgba(226, 232, 240, 0.28)'
                : 'rgba(15, 23, 42, 0.16)',
            '& .MuiChip-label': {
              px: 0.8,
              fontSize: '0.64rem',
              fontWeight: 600,
            },
          })}
        />
      </Box>

      <Box display="flex" gap={0.6} flexWrap="wrap">
        {labels.map((label) => (
          <Chip
            key={`${title}-${label}`}
            label={label}
            size="small"
            sx={(theme) => ({
              height: 22,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(255, 255, 255, 0.82)',
              border: '1px solid',
              borderColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(15, 23, 42, 0.08)',
              '& .MuiChip-label': {
                px: 0.95,
                fontSize: '0.68rem',
                fontWeight: 600,
              },
            })}
          />
        ))}
      </Box>
    </Box>
  );
}

function MonitoringDiffSummaryTable({
  diff,
}: {
  diff: GraphMonitoringEntityDetail['diffs'][number];
}) {
  const { t } = useTranslation();
  const rows = getDiffSummaryRows(diff);

  return (
    <TableContainer
      component={Box}
      sx={(theme) => ({
        borderRadius: 2,
        border: '1px solid',
        borderColor:
          theme.palette.mode === 'dark'
            ? 'rgba(148, 163, 184, 0.16)'
            : 'rgba(15, 23, 42, 0.10)',
        backgroundColor:
          theme.palette.mode === 'dark'
            ? 'rgba(15, 23, 42, 0.42)'
            : 'rgba(248, 250, 252, 0.96)',
        overflow: 'hidden',
        '& .MuiTableCell-root': {
          borderBottom: '1px solid',
          borderBottomColor:
            theme.palette.mode === 'dark'
              ? 'rgba(148, 163, 184, 0.14)'
              : 'rgba(15, 23, 42, 0.08)',
        },
        '& .MuiTableHead-root .MuiTableCell-root': {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? 'rgba(148, 163, 184, 0.05)'
              : 'rgba(15, 23, 42, 0.04)',
        },
        '& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root': {
          borderBottom: 'none',
        },
      })}
    >
      <Table size="small" sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                width: '36%',
                fontSize: '0.64rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'text.secondary',
              }}
            >
              {t('monitoring.portfolio.changes')}
            </TableCell>
            <TableCell
              align="center"
              sx={{
                fontSize: '0.64rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'text.secondary',
              }}
            >
              {t('monitoring.portfolio.nodes')}
            </TableCell>
            <TableCell
              align="center"
              sx={{
                fontSize: '0.64rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'text.secondary',
              }}
            >
              {t('monitoring.portfolio.relationships')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.label}>
              <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                {t(`monitoring.changeTypes.${row.label.toLowerCase()}`)}
              </TableCell>
              <TableCell align="center" sx={{ fontSize: '0.72rem', fontWeight: 700 }}>
                {row.nodes}
              </TableCell>
              <TableCell align="center" sx={{ fontSize: '0.72rem', fontWeight: 700 }}>
                {row.relationships}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

interface CompanyDetailsCardProps {
  profile: Profile;
  canShowIvoLicenses?: boolean;
  monitoringError: string | null;
  monitoringLoading: boolean;
  monitoringSaving: boolean;
  isMonitored: boolean;
  onEnableMonitoring: () => void;
  onDisableMonitoring: () => void;
}

export function CompanyDetailsCard({
  profile,
  canShowIvoLicenses = false,
  monitoringError,
  monitoringLoading,
  monitoringSaving,
  isMonitored,
  onEnableMonitoring,
  onDisableMonitoring,
}: CompanyDetailsCardProps) {
  const { t } = useTranslation();
  const handleMonitoringChange = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (checked && !isMonitored) {
      onEnableMonitoring();
    }

    if (!checked && isMonitored) {
      onDisableMonitoring();
    }
  };

  const licenses = getProfileIvoLicenses(profile).sort((left, right) =>
    Number(right.active === true) - Number(left.active === true)
    || right.since.localeCompare(left.since)
    || compareDisplayText(left.licence, right.licence, appI18n.resolvedLanguage),
  );
  const hasVisibleIvoLicenses = canShowIvoLicenses && licenses.length > 0;
  const employmentSupport = hasEmploymentSupportData(profile.employmentSupport) ? profile.employmentSupport : null;

  return (
    <ModernAccordion defaultExpanded={true}>
      <ModernAccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>{t('assessment.details.title')}</Typography>
      </ModernAccordionSummary>
      <ModernAccordionDetails>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box mb={3}>
              <SectionTitle>{t('assessment.details.general')}</SectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <DataLabel>{t('common.fields.organizationNumber')}</DataLabel>
                  <DataValue>{profile.orgNumber}</DataValue>
                </Grid>
                <Grid item xs={6}>
                  <DataLabel>{t('assessment.details.companyName')}</DataLabel>
                  <DataValue>{profile.orgName}</DataValue>
                </Grid>
                <Grid item xs={12}>
                  <DataLabel>{t('assessment.details.address')}</DataLabel>
                  <DataValue>{formatCompanyAddress(profile)}</DataValue>
                </Grid>
                <Grid item xs={6}>
                  <DataLabel>{t(profile.companyDetailsSource === 'stiftelser' ? 'assessment.details.firstSeen' : 'assessment.registrationDate')}</DataLabel>
                  <DataValue>{getDisplayValue(profile.registrationDate)}</DataValue>
                </Grid>
                <Grid item xs={12}>
                  <DataLabel>{t('assessment.details.activities')}</DataLabel>
                  <DataValue sx={{ fontFamily: 'inherit' }}>{getDisplayValue(profile.businessDescription)}</DataValue>
                </Grid>
              </Grid>
            </Box>

            {hasVisibleIvoLicenses && (
              <Box mb={3}>
                <SectionTitle>{t('assessment.licenses.title')}</SectionTitle>
                <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none' }}>
                  <Table size="small" sx={{
                    '@media (max-width: 599px)': {
                      '& thead': { display: 'none' },
                      '& tbody': { display: 'block' },
                      '& tbody tr': { display: 'grid', gridTemplateColumns: '1fr 1fr', p: 1 },
                      '& tbody td': { border: 0, p: 0.5 },
                      '& tbody td:first-of-type, & tbody td:last-of-type': { gridColumn: '1 / -1' },
                    },
                  }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: '0.65rem', fontWeight: 700 }}>{t('assessment.licenses.licence')}</TableCell>
                        <TableCell sx={{ fontSize: '0.65rem', fontWeight: 700 }}>{t('assessment.licenses.status')}</TableCell>
                        <TableCell sx={{ fontSize: '0.65rem', fontWeight: 700 }}>{t('assessment.licenses.since')}</TableCell>
                        <TableCell sx={{ fontSize: '0.65rem', fontWeight: 700 }}>{t('assessment.licenses.observations')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {licenses.map((license) => (
                        <TableRow key={`${license.licence}-${license.since}-${license.updated}`}>
                          <TableCell sx={{ fontSize: '0.7rem' }}>{license.licence}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              variant="outlined"
                              color={license.active === true ? 'success' : 'default'}
                              label={t(`assessment.licenses.${license.active === true ? 'active' : license.active === false ? 'inactive' : 'unknown'}`)}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.7rem', whiteSpace: { xs: 'normal', sm: 'nowrap' } }}>
                            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                              {t('assessment.licenses.since')}: {' '}
                            </Box>
                            <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{license.since}</Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.7rem' }}>
                            <Box>{t('assessment.licenses.updated')}: <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{license.updated}</Box></Box>
                            {license.active === false && license.inactiveSince && (
                              <Box>{t('assessment.licenses.inactiveSince')}: <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{license.inactiveSince}</Box></Box>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {t('assessment.licenses.activityNote')}
                </Typography>
              </Box>
            )}

            {employmentSupport && (
              <Box mb={3}>
                <SectionTitle>{t('employmentSupport.summaryTitle')}</SectionTitle>
                <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                  <DataValue sx={{ fontWeight: 600 }}>
                    {formatNumber(employmentSupport.amountLast12Months, appI18n.resolvedLanguage, { style: 'currency', currency: 'SEK', maximumFractionDigits: 2 })}
                  </DataValue>
                  <Typography variant="caption" color="text.secondary">{t('employmentSupport.summaryPeriod')}</Typography>
                </Box>
              </Box>
            )}

            <Box>
              <SectionTitle>{t('assessment.details.companyStatuses')}</SectionTitle>
              {profile.bolagsverket?.companyStatuses.length === 0 ? (
                <Typography variant="body2" color="text.secondary">{t('assessment.details.noStatuses')}</Typography>
              ) : (
                <Box display="flex" flexDirection="column" gap={0.5}>
                  {profile.bolagsverket?.companyStatuses.map((status: any, index: number) => (
                    <Box key={index} display="flex" justifyContent="space-between" alignItems="baseline">
                      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{status.date}</Typography>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>{getCompanyStatus(status.status)}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box mb={3}>
              <SectionTitle>{t('assessment.details.riskAssessment')}</SectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <DataLabel>{t('assessment.details.riskScore')}</DataLabel>
                    <Tooltip title={t('assessment.details.riskScoreHelp')}>
                      <DataValue sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                         {profile.bolagsverket?.companyStatuses.length !== 0 || (profile.kfm?.totalDebt && parseFloat(profile.kfm.totalDebt) > 0)
                          ? t('common.states.notAvailable')
                          : profile.companyRisk?.prediction
                            ? `${(profile.companyRisk.prediction * 100).toFixed(2)}%`
                            : t('common.states.notAvailable')}
                      </DataValue>
                    </Tooltip>
                  </Box>
                  
                  <Box>
                    <DataLabel sx={{ mb: 0.5 }}>{t('assessment.details.ratingClass')}</DataLabel>
                    <Box display="flex" gap={0.5} width="100%">
                      {['A', 'B', 'C', 'D', 'E'].map((rating) => {
                        const isActive = profile.companyRisk?.ratingClass === rating;
                        const getColor = (r: string) => {
                            if (r === 'E') return '#9d0f54';
                            if (r === 'D') return '#F4B400';
                            if (r === 'C') return '#0040f4';
                            if (r === 'B') return '#00C914';
                            if (r === 'A') return '#0f9d58';
                            return 'grey.400';
                        };
                        const color = getColor(rating);
                        
                        return (
                          <Box
                            key={rating}
                            sx={{
                              flex: 1,
                              height: 28,
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              fontSize: '0.75rem',
                              color: isActive ? 'white' : 'text.disabled',
                              backgroundColor: isActive ? color : 'action.hover',
                              border: `1px solid ${isActive ? color : 'rgba(0,0,0,0.08)'}`,
                              opacity: isActive ? 1 : 0.7,
                            }}
                          >
                            {rating}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>

                  <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                    <DataLabel>{t('assessment.details.totalDebt')}</DataLabel>
                    <DataValue sx={{ fontWeight: 600, color: profile.kfm?.totalDebt ? 'error.main' : 'success.main' }}>
                      {`${formatAmount(profile.kfm?.totalDebt || '0')} SEK`}
                    </DataValue>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Box mb={3}>
              <SectionTitle>{t('navigation.monitoring')}</SectionTitle>
              <MonitoringToggleCard
                sx={(theme) => ({
                  borderColor: isMonitored ? 'rgba(76, 175, 80, 0.38)' : theme.palette.divider,
                  backgroundColor: isMonitored
                    ? theme.palette.mode === 'dark'
                      ? 'rgba(76, 175, 80, 0.10)'
                      : 'rgba(76, 175, 80, 0.07)'
                    : undefined,
                  boxShadow: isMonitored ? '0 0 0 1px rgba(76, 175, 80, 0.08) inset' : 'none',
                })}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, minWidth: 0 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, lineHeight: 1.2 }}>
                      {t(isMonitored ? 'monitoring.detail.on' : 'monitoring.detail.off')}
                    </Typography>

                  </Box>
                </Box>
                <Switch
                  checked={isMonitored}
                  disabled={monitoringLoading || monitoringSaving}
                  onChange={handleMonitoringChange}
                  name="companyMonitoring"
                  size="small"
                  sx={(theme) => ({
                    mr: -0.25,
                    '&& .MuiSwitch-track': {
                      backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.30)'
                        : 'rgba(15, 23, 42, 0.32)',
                      opacity: 1,
                    },
                    '& .MuiSwitch-thumb': {
                      boxShadow: 'none',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: 'success.main',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: 'success.main',
                      opacity: 1,
                    },
                  })}
                />
              </MonitoringToggleCard>
              {monitoringError && (
                <Alert severity="warning" sx={{ mt: 1, py: 0, '& .MuiAlert-message': { fontSize: '0.72rem' } }}>
                  {monitoringError}
                </Alert>
              )}
            </Box>

            <Box>
              <SectionTitle>{t('assessment.details.auditorData')}</SectionTitle>
              {profile.revisorData && profile.revisorData.length > 0 ? (
                <Box>
                  {(() => {
                    const activeRevisor = profile.revisorData.find(revisor => !revisor.revisor_end_date);
                    const previousRevisors = profile.revisorData.filter(revisor => revisor.revisor_end_date);
                    
                    return (
                      <>
                        {activeRevisor && (
                          <Box mb={previousRevisors.length > 0 ? 2 : 0}>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.5 }}>
                              <Trans i18nKey="assessment.details.auditedBy" values={{ name: activeRevisor.revisor_name?.trim() || '-', date: activeRevisor.revisor_start_date, count: activeRevisor.RevisorNetwork_CompanyCount_3y || 0 }} components={{ auditor: <AuditorName revisor={activeRevisor} /> }} />
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.5, mt: 1 }}>
                              {(() => {
                                const debtRatio = (activeRevisor.RevisorNetwork_SkuldsaldoCompanyCount_12m_ratio || 0) * 100;
                                const avgDebtRatio = 3.53;
                                const isHighRisk = debtRatio > avgDebtRatio * 1.5;
                                const riskText = t(isHighRisk ? 'assessment.details.significantlyHigher' : 'assessment.details.comparable');
                                
                                const getDebtColor = () => {
                                  if (isHighRisk) return 'error.main';
                                  if (debtRatio < avgDebtRatio) return 'success.main';
                                  return 'warning.main';
                                };

                                return (
                                  <>
                                    {t('assessment.details.auditorDebtPrefix')} <Box component="span" sx={{ color: getDebtColor(), fontWeight: 600 }}>{(debtRatio).toFixed(1)}%</Box> {t('assessment.details.auditorDebtSuffix', { comparison: riskText, average: avgDebtRatio })}
                                  </>
                                );
                              })()}
                            </Typography>
                          </Box>
                        )}

                        {previousRevisors.length > 0 && (
                          <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: 'var(--font-size-xs)', display: 'block', mb: 0.5 }}>
                              {t('assessment.details.previousAuditors')}
                            </Typography>
                            <Box display="flex" flexDirection="column" gap={0.5}>
                              {previousRevisors.map((revisor, index) => (
                                <Typography key={index} variant="body2" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                  <AuditorName revisor={revisor} /> ({revisor.revisor_start_date} - {revisor.revisor_end_date})
                                </Typography>
                              ))}
                            </Box>
                          </Box>
                        )}
                      </>
                    );
                  })()}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">{t('assessment.details.noAuditor')}</Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </ModernAccordionDetails>
    </ModernAccordion>
  );
}

interface CompanyMonitoringAccordionProps {
  monitoringDetail: GraphMonitoringEntityDetail;
  monitoringError: string | null;
}

function CompanyMonitoringAccordion({
  monitoringDetail,
  monitoringError,
}: CompanyMonitoringAccordionProps) {
  const { t } = useTranslation();
  const notificationDiffs = getNotificationDiffs(monitoringDetail);
  const triggeredRuleLabels = getTriggeredRuleLabels(monitoringDetail);
  const ruleLookup = new Map(
    monitoringDetail.ruleSubscriptions.map((rule) => [rule.ruleKey, rule.displayName]),
  );

  return (
    <ModernAccordion defaultExpanded={true}>
      <ModernAccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>{t('assessment.details.monitoringNotifications')}</Typography>
      </ModernAccordionSummary>
      <ModernAccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {monitoringError && <Alert severity="warning">{monitoringError}</Alert>}

          {triggeredRuleLabels.length > 0 && (
            <Box>
              <MonitoringRuleList
                title={t('monitoring.rulesTriggered')}
                labels={triggeredRuleLabels}
              />
            </Box>
          )}

          <Box>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                textTransform: 'uppercase',
                fontSize: 'var(--font-size-xs)',
                display: 'block',
                mb: 0.75,
              }}
            >
              {t('assessment.details.recentNotifications')}
            </Typography>

            {notificationDiffs.length > 0 ? (
              <Box display="flex" flexDirection="column" gap={1}>
                {notificationDiffs.map((diff) => {
                  const diffRuleLabels = diff.ruleMatches.map((ruleKey) =>
                    getMonitoringRuleName(ruleKey, ruleLookup.get(ruleKey) ?? humanizeValue(ruleKey)),
                  );

                  return (
                    <MonitoringNotificationCard key={diff.id} variant="outlined">
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            fontSize: '0.62rem',
                            color: 'text.secondary',
                          }}
                        >
                          {t('assessment.details.changeBreakdown')}
                        </Typography>
                        <MonitoringDiffSummaryTable diff={diff} />
                        {diffRuleLabels.length === 0 && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.66rem', lineHeight: 1.45 }}
                          >
                            {diff.changeSummary}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ mt: 0.8 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.66rem' }}>
                          {formatDateTime(diff.detectedAt)}
                          {formatRelativeTime(diff.detectedAt)
                            ? ` (${formatRelativeTime(diff.detectedAt)})`
                            : ''}
                        </Typography>
                      </Box>
                    </MonitoringNotificationCard>
                  );
                })}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.45 }}>
                {t('assessment.details.noNotifications')}
              </Typography>
            )}
          </Box>
        </Box>
      </ModernAccordionDetails>
    </ModernAccordion>
  );
}

function CompanyDebtCard({ profile }: { profile: Profile }) {
  const { t } = useTranslation();
  const hasPublicDebts = profile.kfm?.publicDebts && profile.kfm?.publicDebts.length > 0;
  const hasPrivateDebts = profile.kfm?.privateDebt && profile.kfm.privateDebt.count > 0;
  const hasPaymentOrders = profile.kfm?.privateDebtCases && profile.kfm?.privateDebtCases?.length > 0;
  const hasPublicDebtCases = profile.kfm?.publicDebtCases && profile.kfm?.publicDebtCases?.length > 0;

  return (
    <ModernAccordion defaultExpanded={true}>
      <ModernAccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>{t('assessment.details.debtAnalysis')}</Typography>
      </ModernAccordionSummary>
      <ModernAccordionDetails>
        <Box mb={3}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
               <SectionTitle sx={{ mb: 0, borderBottom: 'none' }}>{t('assessment.details.totalDebt')}</SectionTitle>
            </Grid>
            <Grid item xs={12} sm={6}>
               <Typography variant="h4" sx={{ fontWeight: 600, color: profile.kfm?.totalDebt ? 'error.main' : 'success.main', textAlign: 'right' }}>
                  {`${formatAmount(profile.kfm?.totalDebt || '0')} SEK`}
               </Typography>
            </Grid>
          </Grid>
        </Box>

        {hasPublicDebts && (
          <Box mb={3}>
            <SectionTitle>{t('assessment.details.publicDebts')}</SectionTitle>
            <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'action.hover' }}>
                    <TableCell sx={{ fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>{t('assessment.debt.category')}</TableCell>
                    <TableCell sx={{ fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>{t('assessment.debt.authority')}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>{t('assessment.details.count')}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>{t('personSearch.debt')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {profile.kfm?.publicDebts?.map((debt, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ fontSize: 'var(--font-size-xs)' }}>{debt.category}</TableCell>
                      <TableCell sx={{ fontSize: 'var(--font-size-xs)' }}>{debt.authority}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 'var(--font-size-xs)' }}>{debt.count}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: 'var(--font-size-xs)' }}>{formatAmount(debt.debt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {hasPrivateDebts && (
          <Box mb={3}>
            <SectionTitle>{t('assessment.details.privateDebts')}</SectionTitle>
            <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'action.hover' }}>
                    <TableCell sx={{ fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>{t('common.fields.type')}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>{t('assessment.details.count')}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>{t('personSearch.debt')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontSize: 'var(--font-size-xs)' }}>{t('assessment.details.privateClaims')}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 'var(--font-size-xs)' }}>{profile.kfm?.privateDebt?.count}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: 'var(--font-size-xs)' }}>{formatAmount(profile.kfm?.privateDebt?.debt)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <SectionTitle>{t('assessment.details.paymentOrders')}</SectionTitle>
            {!hasPaymentOrders ? (
              <Typography variant="body2" color="text.secondary">{t('assessment.details.noPaymentOrders')}</Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none', maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'action.hover' }}>
                      <TableCell sx={{ fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>{t('common.fields.date')}</TableCell>
                      <TableCell sx={{ fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>{t('assessment.details.creditor')}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>{t('assessment.debt.amount')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {profile.kfm?.privateDebtCases?.map((debt, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ fontSize: 'var(--font-size-xs)' }}>{debt.date}</TableCell>
                        <TableCell sx={{ fontSize: 'var(--font-size-xs)' }}>{debt.creditor}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: 'var(--font-size-xs)' }}>{formatAmount(debt.debt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionTitle>{t('assessment.details.publicDebtCases')}</SectionTitle>
            {!hasPublicDebtCases ? (
              <Typography variant="body2" color="text.secondary">{t('assessment.details.noPublicDebtCases')}</Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none', maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableRow sx={{ backgroundColor: 'action.hover' }}>
                    <TableCell sx={{ fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>{t('common.fields.date')}</TableCell>
                    <TableCell sx={{ fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>{t('assessment.debt.authority')}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>{t('assessment.debt.amount')}</TableCell>
                  </TableRow>
                  <TableBody>
                    {profile.kfm?.publicDebtCases?.map((debt, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ fontSize: 'var(--font-size-xs)' }}>{debt.date}</TableCell>
                        <TableCell sx={{ fontSize: 'var(--font-size-xs)' }}>{debt.authority}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: 'var(--font-size-xs)' }}>{formatAmount(debt.debt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid>
        </Grid>
      </ModernAccordionDetails>
    </ModernAccordion>
  );
}

function DebtOverTimeViz({ data, privateDebtCases, publicDebtCases }: { data: any, privateDebtCases: any, publicDebtCases: any }) {
  const { t } = useTranslation();
  // Create unified data structure
  const unifiedDebtCases = [
    // Process private debt cases
    ...(privateDebtCases?.map((debt: any) => ({
      date: debt.date,
      type: "private" as const,
      status: debt.status || "", // ansökan/utslag if available
      authority: debt.creditor || "",
      debt: debt.debt
    })) || []),
    // Process public debt cases
    ...(publicDebtCases?.map((debt: any) => ({
      date: debt.date,
      type: "public" as const,
      status: "", // empty string for public debt cases
      authority: debt.authority || "",
      debt: debt.debt
    })) || [])
  ];

  return (
    <ModernAccordion defaultExpanded={true}>
      <ModernAccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>{t('assessment.details.debtTrend')}</Typography>
      </ModernAccordionSummary>
      <ModernAccordionDetails>
        <Box sx={{ width: '100%' }}>
          <TimeSeriesChart data={data} debtCases={unifiedDebtCases} />
        </Box>
      </ModernAccordionDetails>
    </ModernAccordion>
  );
}

function RelationshipsCard({ profile }: { profile: Profile }) {
  const { t } = useTranslation();
  const nodes = profile.graphData.nodes;
  const relationships = profile.graphData.relationships.filter((relationship: Edge) => 
    relationship.metadata.cumulativeRelationshipTypes.flat().includes("FUNKTIONAR")
  );
  const sourceNodeId = profile.graphData.metadata.sourceNodeId;

  return (
    <ModernAccordion defaultExpanded={true}>
      <ModernAccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>{t('assessment.details.connectedRelationships')}</Typography>
      </ModernAccordionSummary>
      <ModernAccordionDetails>
        <RelationshipsTable
          nodes={nodes}
          relationships={relationships}
          sourceNodeId={sourceNodeId}
        />
      </ModernAccordionDetails>
    </ModernAccordion>
  );
}

interface CompanyDetailsPanelProps {
    profile: Profile;
    canShowIvoLicenses?: boolean;
    monitoringDetail: GraphMonitoringEntityDetail | null;
    monitoringError: string | null;
    monitoringLoading: boolean;
    monitoringSaving: boolean;
    isMonitored: boolean;
    onEnableMonitoring: () => void;
    onDisableMonitoring: () => void;
    onClose?: () => void;
}

export default function CompanyDetailsPanel({
  profile,
  canShowIvoLicenses = false,
  monitoringDetail,
  monitoringError,
  monitoringLoading,
  monitoringSaving,
  isMonitored,
  onEnableMonitoring,
  onDisableMonitoring,
  onClose,
}: CompanyDetailsPanelProps) {
  const { t } = useTranslation();
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
        <Box sx={{ px: 2, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
            <Typography variant="subtitle2" fontWeight="bold">{t('assessment.details.panelTitle')}</Typography>
            {onClose && (
                <IconButton size="small" onClick={onClose} aria-label={t('common.actions.close')}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            )}
        </Box>
        <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', p: 1.5 }}>
            <CompanyDetailsCard
                profile={profile}
                canShowIvoLicenses={canShowIvoLicenses}
                monitoringError={monitoringError}
                monitoringLoading={monitoringLoading}
                monitoringSaving={monitoringSaving}
                isMonitored={isMonitored}
                onEnableMonitoring={onEnableMonitoring}
                onDisableMonitoring={onDisableMonitoring}
            />
            <DebtOverTimeViz 
                data={profile.kfm?.debtHistory} 
                privateDebtCases={profile.kfm?.privateDebtCases}
                publicDebtCases={profile.kfm?.publicDebtCases}
            />
            <EmploymentSupportCard data={profile.employmentSupport} />
            <CompanyDebtCard profile={profile} />
            {isMonitored && monitoringDetail && (
                <CompanyMonitoringAccordion
                    monitoringDetail={monitoringDetail}
                    monitoringError={monitoringError}
                />
            )}
            <RelationshipsCard profile={profile} />
        </Box>
    </Box>
  );
}
